/**
 * User Settings V2 — Danger Zone: soft-delete or restore the account being
 * edited. Not part of the Save bar's diff because these are lifecycle
 * actions with side effects (sign-out, email release, reset email), not
 * field edits; each one runs a preflight and asks for confirmation first.
 *
 * Contains:
 * - UserDangerZone (default export)
 */

import { Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DANGER_ZONE_CONTROL_WIDTH, SettingsDangerZone, SettingsFieldRow } from '@sophocles/views/settings/components';
import { lifecycleBlockerMessage } from '@sophocles/views/users/lifecycle/lifecycleMessages';
import { LIFECYCLE_ACTIONS, lifecycleActionFor, originalEmail } from '@sophocles/views/users/lifecycle/userLifecycle';
import UserLifecycleDialog from '@sophocles/views/users/lifecycle/UserLifecycleDialog';
import useUserLifecycleAction from '@sophocles/views/users/lifecycle/useUserLifecycleAction';

/**
 * Rendered only for requesters who hold `permissions.canDeleteUsers`; the
 * page decides that.
 *
 * @param {object} props
 * @param {number} props.userId — the account being edited (never the requester)
 * @param {object} props.permissions — the saved `permissions` settings section
 * @param {string | null | undefined} props.email — the saved `profile.email`
 * @param {() => void} [props.onChanged] — called after a delete or restore lands
 */
export default function UserDangerZone({ userId, permissions, email, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { run, submitting } = useUserLifecycleAction();
  const userIds = useMemo(() => [userId], [userId]);

  const action = lifecycleActionFor(permissions);
  const isDelete = action === LIFECYCLE_ACTIONS.DELETE;
  const targetIsSuperuser = !!permissions.is_superuser;
  const restoredEmail = originalEmail(email, userId);

  const handleConfirm = () =>
    run(action, { userIds }).then(outcome => {
      if (!outcome?.applied) return;
      setOpen(false);
      onChanged?.();
    });

  const zoneDescription = isDelete
    ? t('userSettings.v2.dangerZoneDescription', 'Delete this user account, or restore one that was deleted.')
    : t(
        'userSettings.v2.lifecycle.statusDeleted',
        'This account was deleted. Its email address, {{email}}, is free for another account until it is restored.',
        { email: restoredEmail },
      );

  let rowDescription;
  if (!isDelete) {
    rowDescription = t(
      'userSettings.v2.lifecycle.restoreDescription',
      'Reactivates the account under its original email address and emails the user a password reset.',
    );
  } else if (targetIsSuperuser) {
    rowDescription = lifecycleBlockerMessage(t, 'superuser_excluded');
  } else {
    rowDescription = t(
      'userSettings.v2.lifecycle.deleteDescription',
      'Signs the user out, invalidates their password and releases their email address. The account stays on record and can be restored.',
    );
  }

  return (
    <>
      <SettingsDangerZone description={zoneDescription}>
        <SettingsFieldRow
          label={
            isDelete
              ? t('userSettings.v2.lifecycle.deleteLabel', 'Delete user')
              : t('userSettings.v2.lifecycle.restoreLabel', 'Restore user')
          }
          description={rowDescription}
          controlWidth={DANGER_ZONE_CONTROL_WIDTH}
        >
          <Button
            variant={isDelete ? 'contained' : 'outlined'}
            color={isDelete ? 'error' : 'success'}
            disabled={submitting || (isDelete && targetIsSuperuser)}
            onClick={() => setOpen(true)}
          >
            {isDelete ? t('actions.delete') : t('actions.restore')}
          </Button>
        </SettingsFieldRow>
      </SettingsDangerZone>
      <UserLifecycleDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        action={action}
        userIds={userIds}
        email={restoredEmail}
        submitting={submitting}
      />
    </>
  );
}
